import { useEffect, useMemo, useState } from "react";
import api, { naira, usd } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LaunchPartyBanner from "@/components/LaunchPartyBanner";
import { Users, Sparkles, Copy, Check, Clock, ExternalLink, PlusCircle, Wallet, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import RefundNote from "@/components/RefundNote";

/**
 * Watch Party purchase + share dialog. Two visual states:
 *   1. Configure & pay — pick a package (price = PPV × viewers × multiplier), hit Pay.
 *   2. Share — after payment success (via callback), show the guest link
 *      + copy button + WhatsApp share, plus expiry countdown.
 *
 * ``activeParty`` (optional) skips straight to state 2 when the host
 * already has an unexpired party for this film — we don't want them to
 * pay twice.
 */
export default function WatchPartyDialog({ open, onOpenChange, film, currency, activeParty }) {
  const { user, setUser } = useAuth();
  const [packages, setPackages] = useState(null);
  const [pkgId, setPkgId] = useState("mini");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareState, setShareState] = useState(activeParty || null);

  useEffect(() => {
    if (activeParty) setShareState(activeParty);
  }, [activeParty]);

  // Server prices every package from the film's PPV price; it re-computes authoritatively on initiate.
  useEffect(() => {
    if (!open || !film?.id || shareState) return;
    api.get("/watch-party/packages", { params: { film_id: film.id, currency } })
      .then(({ data }) => setPackages(data))
      .catch(() => toast.error("Couldn't load Watch Party packages"));
  }, [open, film?.id, currency, shareState]);

  const fmt = (v) => (currency === "USD" ? usd(v / 100) : naira(v));
  const selected = packages?.packages.find((p) => p.id === pkgId);
  const price = selected?.price || 0;
  const priceLabel = fmt(price);

  // Wallet balance for the active currency. Refreshed off the AuthContext
  // user object, which is populated on every /auth/me tick.
  const walletBalance = currency === "USD"
    ? (user?.wallet_balance_usd_cents || 0)
    : (user?.wallet_balance_naira || 0);
  const canPayWithWallet = walletBalance >= price;
  const walletBalanceLabel = currency === "USD" ? usd(walletBalance / 100) : naira(walletBalance);
  const shortfall = Math.max(price - walletBalance, 0);
  const launch = film?.publishing_state === "coming_soon";
  const reservedPrice = (currency === "USD" ? shareState?.amount_usd_cents : shareState?.amount_naira) || 0;

  const initiate = async ({ useWallet, payLater = false, shareToken = null }) => {
    setLoading(true);
    try {
      const callbackUrl = `${window.location.origin}/payment/callback?party=1`;
      const { data } = await api.post("/watch-party/initiate", {
        film_id: film.id,
        package_id: pkgId,
        currency,
        callback_url: callbackUrl,
        use_wallet: !!useWallet,
        launch,
        pay_later: payLater,
        share_token: shareToken,
      });
      if (data.reserved) {
        setShareState({ ...data, launch: true, amount_naira: currency === "NGN" ? data.price : null, amount_usd_cents: currency === "USD" ? data.price : null });
        toast.success("Launch Watch Party reserved — pay any time before it opens on release day.");
        setLoading(false);
        return;
      }
      // ----- WALLET PATH -----
      // Backend already fulfilled the party (creator share credited,
      // expiry set). Land straight in the share state with the link
      // ready to copy — no processor redirect, no callback bounce.
      if (data.wallet_paid) {
        // Trim the wallet locally so the dialog reflects the new balance
        // without waiting for a full /auth/me round trip.
        const paid = shareToken ? reservedPrice : price;
        if (setUser && user) {
          if (currency === "USD") {
            setUser({ ...user, wallet_balance_usd_cents: walletBalance - paid });
          } else {
            setUser({ ...user, wallet_balance_naira: walletBalance - paid });
          }
        }
        setShareState({
          share_token: data.share_token,
          guest_count: data.guest_count,
          viewer_limit: data.viewer_limit,
          launch: launch || !!shareToken,
          status: launch ? "scheduled" : "active",
          expires_at: launch ? null : new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
        });
        toast.success(launch ? "Paid — your Launch Watch Party opens at 8:00 AM WAT on release day." : "Paid with wallet — Watch Party is live.");
        setLoading(false);
        return;
      }
      // ----- CARD PATH -----
      // Persist a hint so the callback page knows what to verify.
      try {
        window.sessionStorage.setItem("cv_pending_watch_party", data.reference);
      } catch { /* private tab — the URL params still carry the reference */ }
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start Watch Party payment");
      setLoading(false);
    }
  };

  const [restarting, setRestarting] = useState(false);
  const restart = async () => {
    setRestarting(true);
    try {
      const { data } = await api.post(`/watch-party/${shareState.share_token}/restart`);
      setShareState((st) => ({ ...st, status: data.status }));
      toast.success("Watch Party restarted — share the same link again");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't restart the party");
    } finally {
      setRestarting(false);
    }
  };

  const shareUrl = shareState
    ? `${window.location.origin}/watch-party/${shareState.share_token}`
    : "";

  const expiryLabel = useMemo(() => {
    if (!shareState?.expires_at) return "";
    const diff = new Date(shareState.expires_at).getTime() - Date.now();
    if (diff <= 0) return "Expired";
    const hours = Math.floor(diff / 3_600_000);
    const minutes = Math.floor((diff % 3_600_000) / 60_000);
    return hours > 0 ? `${hours}h ${minutes}m left` : `${minutes}m left`;
  }, [shareState]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Watch Party link copied");
    } catch {
      toast.error("Copy failed — long-press the link to copy manually");
    }
  };

  const whatsappUrl = shareState
    ? `https://wa.me/?text=${encodeURIComponent(
        `Join me on Cinevoya to watch "${film?.title}" together — ${shareUrl}`
      )}`
    : "";

  // Kick off an extension purchase. Server prices +12h/+24h; we just
  // redirect the host to the processor and let ``PaymentCallback``
  // handle the ``cvwpx_`` prefix on return.
  const extend = async (hours) => {
    try {
      const callbackUrl = `${window.location.origin}/payment/callback?party=1`;
      const { data } = await api.post(`/watch-party/${shareState.share_token}/extend`, {
        hours,
        callback_url: callbackUrl,
      });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start extension");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-white/10 text-foreground max-w-md" data-testid="watch-party-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-terracotta" />
            <p className="text-xs uppercase tracking-widest text-terracotta font-semibold">
              {shareState ? "Watch Party Ready" : "Watch Party"}
            </p>
          </div>
          <DialogTitle className="font-display text-2xl font-black leading-tight">
            {shareState ? (shareState.launch && shareState.status !== "active" ? "Your Launch Watch Party is booked." : "Share the link — everyone can watch.") : launch ? `Book a Launch Watch Party for “${film?.title}”` : `Host a Watch Party for “${film?.title}”`}
          </DialogTitle>
        </DialogHeader>

        {shareState ? (
          <div className="space-y-4 mt-2 min-w-0">
            {shareState.launch && ["scheduled", "reserved", "pending"].includes(shareState.status) && (
              <LaunchPartyBanner
                state={shareState}
                releaseDate={film?.release_date}
                busy={loading}
                canWallet={walletBalance >= reservedPrice}
                priceLabel={fmt(reservedPrice)}
                onPay={(useWallet) => initiate({ useWallet, shareToken: shareState.share_token })}
              />
            )}
            {shareState.status === "ended" && (
              <div className="rounded-xl border border-gold/30 bg-gold/5 p-4 flex items-center justify-between gap-3" data-testid="watch-party-ended-banner">
                <p className="text-xs text-foreground/75">You ended this party. Your time is still running — restart it any time before it expires.</p>
                <Button type="button" size="sm" onClick={restart} disabled={restarting} className="bg-gold text-ink hover:bg-gold/90 shrink-0" data-testid="watch-party-restart-btn">
                  {restarting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Restart
                </Button>
              </div>
            )}
            <div className="rounded-xl border border-terracotta/30 bg-terracotta/5 p-4">
              <p className="text-[10px] uppercase tracking-widest text-terracotta font-semibold mb-1.5">Shareable link</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 text-xs text-foreground/90 font-mono truncate" data-testid="watch-party-share-url">
                  {shareUrl}
                </code>
                <Button
                  type="button"
                  size="sm"
                  onClick={copyLink}
                  className="h-8 px-3 bg-terracotta hover:bg-ember text-white shrink-0"
                  data-testid="watch-party-copy-btn"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs text-foreground/70">
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-gold" />
                {expiryLabel}
              </span>
              <span>{shareState.viewer_limit ? <>Up to <strong className="text-foreground">{shareState.viewer_limit}</strong> viewers incl. you</> : <>Up to <strong className="text-foreground">{shareState.guest_count}</strong> guests</>}</span>
            </div>

            {/* Extension top-up — bought in +12h chunks. Host gets the
                same share link back, guests keep chatting/watching
                without a re-invite. */}
            {!(shareState.launch && shareState.status !== "active") && (
            <div className="rounded-xl border border-white/10 bg-black/30 p-3">
              <div className="flex items-center gap-2 mb-2">
                <PlusCircle className="w-3.5 h-3.5 text-terracotta" />
                <p className="text-[10px] uppercase tracking-widest text-terracotta font-semibold">Need more time?</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => extend(12)}
                  className="h-8 border border-white/10 hover:bg-white/5 text-xs"
                  data-testid="watch-party-extend-12"
                >
                  +12h · {currency === "USD" ? "$5" : "₦500"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => extend(24)}
                  className="h-8 border border-white/10 hover:bg-white/5 text-xs"
                  data-testid="watch-party-extend-24"
                >
                  +24h · {currency === "USD" ? "$10" : "₦1,000"}
                </Button>
              </div>
            </div>
            )}
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full h-11 rounded-lg bg-[#25D366] hover:bg-[#20BD5B] text-white font-semibold transition-colors"
              data-testid="watch-party-whatsapp-btn"
            >
              <ExternalLink className="w-4 h-4" /> Share on WhatsApp
            </a>
            <p className="text-xs text-foreground/50 leading-relaxed">
              Guests join free with a Cinevoya account — first come, first served until your seats are full. The link expires 24h after {shareState.launch ? "the party opens" : "purchase"}.
            </p>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                data-testid="watch-party-close-share"
              >
                Done
              </Button>
              <Button
                onClick={() => window.open(shareUrl, "_blank")}
                disabled={["ended", "scheduled", "reserved", "pending"].includes(shareState.status)}
                className="bg-terracotta hover:bg-ember text-white"
                data-testid="watch-party-open-btn"
              >
                Open party
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <p className="text-sm text-foreground/70 leading-relaxed">
              Pick a package for you and your crew. Send them the link, they click, they're in. Session lasts 24 hours.
            </p>

            {!packages ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-terracotta animate-spin" /></div>
            ) : (
              <div className="grid grid-cols-2 gap-2.5" role="radiogroup" data-testid="watch-party-packages">
                {packages.packages.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={pkgId === p.id}
                    onClick={() => setPkgId(p.id)}
                    className={`text-left rounded-xl border p-3 transition-colors ${pkgId === p.id ? "border-terracotta bg-terracotta/10" : "border-white/10 bg-black/30 hover:border-white/25"}`}
                    data-testid={`watch-party-package-${p.id}`}
                  >
                    <p className="text-sm font-semibold">{p.name}</p>
                    <p className="text-[11px] text-foreground/55 flex items-center gap-1 mt-0.5"><Users className="w-3 h-3" /> {p.viewers} viewers</p>
                    <p className="font-display text-lg font-bold text-gold mt-2 tabular-nums" data-testid={`watch-party-package-${p.id}-price`}>{fmt(p.price)}</p>
                  </button>
                ))}
              </div>
            )}
            {selected && (
              <div className="flex items-baseline justify-between rounded-xl border border-white/10 bg-black/30 px-4 py-3">
                <span className="text-[11px] text-foreground/55" data-testid="watch-party-formula">
                  {fmt(packages.unit_price)} × {selected.viewers} viewers × {selected.multiplier}
                </span>
                <span className="font-display text-2xl font-bold text-gold" data-testid="watch-party-total">{priceLabel}</span>
              </div>
            )}

            <ul className="text-xs text-foreground/60 space-y-1.5 leading-relaxed">
              <li className="flex items-start gap-1.5"><Sparkles className="w-3 h-3 mt-0.5 text-terracotta shrink-0" /> Viewers includes you — guests join free with your link.</li>
              <li className="flex items-start gap-1.5"><Sparkles className="w-3 h-3 mt-0.5 text-terracotta shrink-0" /> Each person plays on their own device.</li>
              <li className="flex items-start gap-1.5"><Sparkles className="w-3 h-3 mt-0.5 text-terracotta shrink-0" /> Live chat runs alongside the film.</li>
            </ul>

            {/* Wallet balance hint — mirrors the FilmDetail rental UI. */}
            <div className="flex items-center gap-1.5 text-xs text-foreground/55" data-testid="watch-party-wallet-hint">
              <Wallet className="w-3 h-3 text-gold" />
              Wallet balance: <strong className="text-foreground/80">{walletBalanceLabel}</strong>
              {canPayWithWallet ? (
                <span className="text-emerald-400 ml-1">· enough to pay from wallet</span>
              ) : (
                <span className="ml-1">
                  · top up {currency === "USD" ? usd(shortfall / 100) : naira(shortfall)} more to pay from wallet, or use card
                </span>
              )}
            </div>

            <DialogFooter className="gap-2 flex-col sm:flex-row">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={loading}
                data-testid="watch-party-cancel"
              >
                Cancel
              </Button>
              {canPayWithWallet && (
                <Button
                  onClick={() => initiate({ useWallet: true })}
                  disabled={loading || !selected}
                  className="bg-gold hover:bg-gold/90 text-ink"
                  data-testid="watch-party-wallet-btn"
                >
                  <Wallet className="w-4 h-4 mr-1" />
                  {loading ? "Charging wallet…" : `Pay ${priceLabel} from wallet`}
                </Button>
              )}
              <Button
                onClick={() => initiate({ useWallet: false })}
                disabled={loading || !selected}
                className="bg-terracotta hover:bg-ember text-white"
                data-testid="watch-party-pay-btn"
              >
                {loading ? "Redirecting…" : `Pay ${priceLabel}${canPayWithWallet ? " with card" : ""}`}
              </Button>
              {launch && (
                <Button
                  variant="outline"
                  onClick={() => initiate({ useWallet: false, payLater: true })}
                  disabled={loading || !selected}
                  className="border-sky-400/40 text-sky-200 hover:bg-sky-500/10"
                  data-testid="watch-party-reserve-btn"
                >
                  Reserve · pay on release day
                </Button>
              )}
            </DialogFooter>
            <RefundNote className="text-center sm:text-right" testId="watch-party-refund-policy-link" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
